import { ReactNode, useMemo, useState, useCallback } from 'react'

import { RemoveCircleOutline } from '@mui/icons-material'
import { Box, FormHelperText, IconButton, Stack, Tooltip } from '@mui/material'
import { TextField } from '@workwave-tidal/core/components'

import { CustomFieldOption } from '../../../../../../types'
import { INITIAL_FIELD_KEY, MAX_CUSTOM_FIELD_NAME_LENGTH } from '../../constants'

import FreeSelect from './components/FreeSelect'

import { useTexts } from './useTexts'

type FieldErrors = Record<string, { name?: string; value?: string }>
// Use index as key for touched state to persist across key changes
type TouchedState = Record<number, boolean>

interface Props {
  onUpdateSelect: (newValue: string, index: number) => void
  onUpdateValue: (newValue: string, index: number) => void
  onRemove: (index) => void
  value: Record<string, string>
  disabled?: boolean
  options: CustomFieldOption[]
  errorText: ReactNode
  error: boolean
}

export function Field(props: Props) {
  const { value, onRemove, disabled, onUpdateSelect, onUpdateValue, options, errorText, error } =
    props

  const texts = useTexts()

  const nameMaxLengthErrorText = texts.getMaxLengthText(MAX_CUSTOM_FIELD_NAME_LENGTH)

  // Track which name fields have been touched (blurred) - keyed by index
  const [nameTouched, setNameTouched] = useState<TouchedState>({})
  // Track which value fields have been touched (blurred) - keyed by index
  const [valueTouched, setValueTouched] = useState<TouchedState>({})

  const handleNameBlur = useCallback((index: number) => {
    setNameTouched(prev => ({
      ...prev,
      [index]: true,
    }))
  }, [])

  const handleValueBlur = useCallback((index: number) => {
    setValueTouched(prev => ({
      ...prev,
      [index]: true,
    }))
  }, [])

  // Wrap onRemove to also shift touched state when an item is removed
  const handleRemove = useCallback(
    (index: number) => {
      // Shift touched state: items after the removed index move down
      const shiftTouched = (prev: TouchedState): TouchedState => {
        const newTouched: TouchedState = {}
        Object.entries(prev).forEach(([idx, state]) => {
          const numIdx = Number(idx)
          if (numIdx < index) {
            newTouched[numIdx] = state
          } else if (numIdx > index) {
            newTouched[numIdx - 1] = state
          }
          // Skip the removed index
        })
        return newTouched
      }
      setNameTouched(shiftTouched)
      setValueTouched(shiftTouched)
      onRemove(index)
    },
    [onRemove],
  )

  // Parse errorText: either Record<string, { name?, value? }> or a plain string
  const { fieldErrors, generalError } = useMemo(() => {
    if (typeof errorText === 'string') {
      try {
        const parsed = JSON.parse(errorText) as FieldErrors
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          return { fieldErrors: parsed, generalError: undefined }
        }
        // Parsed but not an object - treat as general error
        return { fieldErrors: {} as FieldErrors, generalError: errorText }
      } catch {
        // Not valid JSON - treat as general error
        return { fieldErrors: {} as FieldErrors, generalError: errorText }
      }
    }
    return { fieldErrors: {} as FieldErrors, generalError: undefined }
  }, [errorText])

  return (
    <Stack spacing={2}>
      {Object.entries(value).map(([key, val], index) => {
        const fieldError = fieldErrors[key]
        const hasNameSelected = key !== INITIAL_FIELD_KEY
        // Show name error only if name field was touched (blurred)
        const showNameError = error && !!fieldError?.name && !!nameTouched[index]
        // Show value error if: (value touched OR name has been selected) AND has error
        const showValueError =
          error && !!fieldError?.value && (!!valueTouched[index] || hasNameSelected)
        return (
          <Stack key={key} direction="row" spacing={1} alignItems="flex-start">
            <Box flex={3}>
              <FreeSelect
                label={texts.fieldName}
                initialOptions={options}
                value={key}
                onChange={newValue => onUpdateSelect(newValue, index)}
                onBlur={() => handleNameBlur(index)}
                selectedValues={Object.keys(value)}
                error={showNameError}
                errorText={showNameError ? fieldError?.name : undefined}
                maxLength={MAX_CUSTOM_FIELD_NAME_LENGTH}
                maxLengthErrorText={nameMaxLengthErrorText}
                autoFocus={key === INITIAL_FIELD_KEY}
              />
            </Box>
            <Tooltip title={val} placement="top" disableFocusListener>
              <Box flex={1}>
                <TextField
                  fullWidth
                  placeholder={texts.fieldValue}
                  disabled={disabled}
                  value={val}
                  onChange={e => onUpdateValue(e.target.value, index)}
                  onBlur={() => handleValueBlur(index)}
                  label={texts.fieldValue}
                  error={showValueError}
                  errorIcon={null}
                  helperText={showValueError ? fieldError?.value : undefined}
                />
              </Box>
            </Tooltip>
            <Box flexShrink={0} display="flex" alignItems="center" height="56px">
              <Tooltip title={texts.deleteTooltip}>
                <IconButton color="error" onClick={() => handleRemove(index)}>
                  <RemoveCircleOutline />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>
        )
      })}
      {generalError && <FormHelperText error>{generalError}</FormHelperText>}
    </Stack>
  )
}
